import { c as _c } from "react/compiler-runtime";
import { SystemAdjustmentPill } from "@/components/common/SystemAdjustmentPill";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
import type { ApiWaveCreditType } from "@/generated/models/ApiWaveCreditType";
import type { ApiWaveLog } from "@/generated/models/ApiWaveLog";
import { isAutoGeneratedHandle, isEthereumAddress } from "@/helpers/AllowlistToolHelpers";
import { formatNumberWithCommas, getTimeAgoShort } from "@/helpers/Helpers";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import { WAVE_VOTING_LABELS } from "@/helpers/waves/waves.constants";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import Image from "next/image";
import Link from "next/link";
interface SingleWaveDropLogProps {
  readonly log: ApiWaveLog;
  readonly creditType: ApiWaveCreditType;
}
interface VoteSummaryProps {
  readonly log: ApiWaveLog;
  readonly creditType: ApiWaveCreditType;
}
interface TimeStampProps {
  readonly createdAt: Date | string;
  readonly iconClassName: string;
  readonly textClassName: string;
}
const ClockIcon = t0 => {
  const $ = _c(3);
  const {
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== className) {
    t2 = <svg className={className} viewBox="0 0 24 24" aria-hidden="true" fill="none">{t1}</svg>;
    $[1] = className;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
const VoteSummary = t0 => {
  const $ = _c(19);
  const {
    log,
    creditType
  } = t0;
  const t1 = log.contents.oldVote === 0 ? "voted" : "changed from";
  let t2;
  if ($[0] !== t1) {
    t2 = <span className="tw-text-sm tw-text-iron-500">{t1}</span>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== log.contents.oldVote) {
    t3 = log.contents.oldVote !== 0 && <span className="tw-whitespace-nowrap tw-text-sm tw-font-medium tw-text-iron-400"><span className="tw-tabular-nums">{formatNumberWithCommas(log.contents.oldVote)}</span>{" "}→</span>;
    $[2] = log.contents.oldVote;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = `tw-whitespace-nowrap tw-text-sm tw-font-medium ${log.contents.newVote > 0 ? "tw-text-emerald-500" : "tw-text-rose-500"}`;
  let t5;
  if ($[4] !== log.contents.newVote) {
    t5 = formatNumberWithCommas(log.contents.newVote);
    $[4] = log.contents.newVote;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t5) {
    t6 = <span className="tw-tabular-nums">{t5}</span>;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = WAVE_VOTING_LABELS[creditType];
  let t8;
  if ($[8] !== t4 || $[9] !== t6 || $[10] !== t7) {
    t8 = <span className={t4}>{t6}{" "}{t7}</span>;
    $[8] = t4;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== log.contents.reason) {
    t9 = log.contents.reason === "CREDIT_OVERSPENT" && <SystemAdjustmentPill />;
    $[12] = log.contents.reason;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t2 || $[15] !== t3 || $[16] !== t8 || $[17] !== t9) {
    t10 = <>{t2}{t3}{t8}{t9}</>;
    $[14] = t2;
    $[15] = t3;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return t10;
};
const TimeStamp = t0 => {
  const $ = _c(10);
  const {
    createdAt,
    iconClassName,
    textClassName
  } = t0;
  let t1;
  if ($[0] !== iconClassName) {
    t1 = <ClockIcon className={iconClassName} />;
    $[0] = iconClassName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== createdAt) {
    t2 = getTimeAgoShort(typeof createdAt === "string" ? new Date(createdAt).getTime() : createdAt.getTime());
    $[2] = createdAt;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2 || $[5] !== textClassName) {
    t3 = <span className={textClassName}>{t2}</span>;
    $[4] = t2;
    $[5] = textClassName;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t3) {
    t4 = <div className="tw-flex tw-items-center tw-gap-1 tw-whitespace-nowrap">{t1}{t3}</div>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
export const SingleWaveDropLog = t0 => {
  const $ = _c(19);
  const {
    log,
    creditType
  } = t0;
  const isMobile = useIsMobileScreen();
  const handleValue = log.invoker.handle ?? "";
  const shouldLimit = isEthereumAddress(handleValue) || isAutoGeneratedHandle(handleValue);
  let t1;
  if ($[0] !== log.invoker.pfp) {
    t1 = log.invoker.pfp ? <Image unoptimized={true} src={getScaledImageUri(log.invoker.pfp, ImageScale.W_AUTO_H_50)} alt="User profile picture" width={28} height={28} className="tw-size-7 tw-flex-shrink-0 tw-rounded-md tw-bg-iron-800 tw-object-contain tw-ring-1 tw-ring-white/10" /> : <div className="tw-size-7 tw-flex-shrink-0 tw-rounded-md tw-bg-iron-800 tw-ring-1 tw-ring-white/10" />;
    $[0] = log.invoker.pfp;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const avatar = t1;
  const t2 = log.invoker.handle ?? log.invoker.id;
  const t3 = `/${log.invoker.handle}`;
  const t4 = `tw-inline-block ${shouldLimit ? "tw-max-w-[8rem] tw-truncate" : ""}`;
  let t5;
  if ($[2] !== log.invoker.handle) {
    t5 = <span className="tw-text-sm tw-font-medium tw-text-iron-50 tw-transition-all tw-duration-300 desktop-hover:group-hover:tw-text-iron-300">{log.invoker.handle}</span>;
    $[2] = log.invoker.handle;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== t4 || $[5] !== t5) {
    t6 = <span className={t4}>{t5}</span>;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t3 || $[8] !== t6) {
    t7 = <Link href={t3} className="tw-group tw-no-underline tw-transition-all tw-duration-300 desktop-hover:hover:tw-opacity-80">{t6}</Link>;
    $[7] = t3;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t2 || $[11] !== t7) {
    t8 = <UserProfileTooltipWrapper user={t2}>{t7}</UserProfileTooltipWrapper>;
    $[10] = t2;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const handleLink = t8;
  let t9;
  if ($[13] !== avatar || $[14] !== creditType || $[15] !== handleLink || $[16] !== isMobile || $[17] !== log) {
    t9 = <div className="tw-p-3 tw-transition-colors hover:tw-bg-white/[0.02]">{isMobile ? <div className="tw-flex tw-gap-3">{avatar}<div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col"><div className="tw-flex tw-items-center tw-justify-between tw-gap-2">{handleLink}<TimeStamp createdAt={log.created_at} iconClassName="tw-w-3 tw-h-3 tw-text-iron-400" textClassName="tw-text-xs tw-font-medium tw-text-iron-400" /></div><div className="tw-mt-1 tw-flex tw-flex-wrap tw-items-center tw-gap-x-1.5 tw-gap-y-1.5"><VoteSummary log={log} creditType={creditType} /></div></div></div> : <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-2"><div className="tw-flex tw-items-center tw-gap-x-2 tw-gap-y-2">{avatar}<div className="tw-inline-flex tw-flex-wrap tw-items-center tw-gap-x-1 tw-gap-y-1">{handleLink}<VoteSummary log={log} creditType={creditType} /></div></div><TimeStamp createdAt={log.created_at} iconClassName="tw-w-3.5 tw-h-3.5 tw-text-iron-600 tw-flex-shrink-0" textClassName="tw-text-xs tw-font-medium tw-text-iron-600" /></div>}</div>;
    $[13] = avatar;
    $[14] = creditType;
    $[15] = handleLink;
    $[16] = isMobile;
    $[17] = log;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  return t9;
};